import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type {
  CreateOrderResultUI,
  CreatedOrderUI,
} from "../../../model/order.model";
import {
  formatDate,
  formatItemTotal,
  formatQty,
  getStatusColor,
  normalizeStatusLabel,
} from "./utilsOrderResultDialog";

import { HeaderDialog } from "./HeaderDialog";

import type { CartItem } from "../../../model/product.cart.model";

type FailedOrderUI = CreatedOrderUI & { errorMessage: string };

type Props = {
  open: boolean;
  result: CreateOrderResultUI | null;
  createdList: CreatedOrderUI[];
  failedList: FailedOrderUI[];
  cart?: CartItem[];
  onClose: () => void;
  onNewOrder: () => void;
  onConsult: (orderCode: string) => void;
  resolveProductName?: (productId: string) => string | undefined;
  resolveBranchName?: () => string | undefined;
};

const TABLE_COLUMNS = [
  { id: "order", label: "Ordem de Venda", align: "left" as const },
  { id: "product", label: "Produto", align: "left" as const },
  { id: "qty", label: "Quantidade (L)", align: "right" as const },
  { id: "price", label: "Preço", align: "right" as const },
  { id: "status", label: "Status", align: "center" as const },
  { id: "branch", label: "Filial", align: "center" as const },
  { id: "date", label: "Data de entrega", align: "center" as const },
  { id: "term", label: "Prazo", align: "center" as const },
];

export function OrderResultDialogDesktop({
  open = true,
  result,
  createdList,
  failedList,
  cart = [],
  onClose,
  onNewOrder,
  onConsult,
  resolveProductName,
  resolveBranchName,
}: Props) {
  const rows = useMemo(() => {
    const branch = resolveBranchName?.() ?? "-";

    const success = createdList.map((o) => ({
      ...o,
      kind: "success" as const,
      statusLabel: normalizeStatusLabel(o.status),
      statusColor: getStatusColor(o.status),
    }));

    const errors = failedList.map((o) => ({
      ...o,
      kind: "error" as const,
      orderCode: o.orderCode || "Não gerada",
      statusLabel: `Erro: ${o.errorMessage}`,
      statusColor: "error.main",
    }));

    const allItems = [...success, ...errors];

    // Fallback: se não houver itens na resposta (erro genérico), usar dados do carrinho
    if (allItems.length === 0 && cart.length > 0) {
      return cart.map((item) => ({
        kind: "error" as const,
        statusColor: "error.main",
        data: [
          "-",
          resolveProductName?.(item.productId) ?? item.description,
          formatQty(item.quantity),
          formatItemTotal(item.unitPrice, item.quantity),
          "Erro",
          branch,
          "-",
          item.deadlinePayment > 0
            ? `${item.deadlinePayment} ${
                item.deadlinePayment === 1 ? "Dia" : "Dias"
              }`
            : "À vista",
        ],
      }));
    }

    return allItems.map((r) => ({
      kind: r.kind,
      statusColor: r.statusColor,
      data: [
        r.orderCode || "-",
        resolveProductName?.(r.productId) ?? r.productId,
        formatQty(r.quantityLiters),
        formatItemTotal(r.price, r.quantityLiters),
        r.statusLabel,
        branch,
        formatDate(r.deliveryDateISO),
        r.paymentLabel ?? "-",
      ],
    }));
  }, [createdList, failedList, cart, resolveProductName, resolveBranchName]);

  const header = useMemo(() => {
    if (!result) return null;

    if (result.overall === "SUCESSO") {
      return {
        title: "Sucesso!",
        subtitle: "Seu pedido foi efetuado com sucesso!",
        severity: "success",
      };
    }

    if (result.overall === "PARCIAL") {
      return {
        title: "Sucesso parcial",
        subtitle: "Alguns itens foram criados, outros falharam.",
        severity: "warning",
      };
    }

    return {
      title: "Erro ao criar pedido",
      subtitle: "Não foi possível processar o pedido. Confira os detalhes:",
      severity: "error",
    };
  }, [result]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Detalhes do Pedido
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        {header && (
          <HeaderDialog
            variant="desktop"
            title={header.title}
            subtitle={header.subtitle}
            severity={header.severity}
          />
        )}

        {rows.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      color: "text.primary",
                      borderBottom: "1px solid #e0e0e0",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  {TABLE_COLUMNS.map((col) => (
                    <TableCell key={col.id} align={col.align}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "& td": {
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                        color: "text.primary",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {row.data.map((value, i) => (
                      <TableCell
                        key={i}
                        align={TABLE_COLUMNS[i].align}
                        sx={{
                          ...(i === 0 && { fontWeight: 600 }),
                          ...(i === 4 && {
                            color:
                              row.kind === "error"
                                ? "error.main"
                                : row.statusColor,
                            fontWeight: 600,
                            whiteSpace: "normal",
                          }),
                        }}
                      >
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box
          mt={3}
          display="flex"
          justifyContent="flex-end"
          gap={3}
          alignItems="center"
        >
          <Button
            variant="outlined"
            color="error"
            onClick={onNewOrder}
            disableElevation
            sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 700 }}
          >
            Criar novo pedido
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 700 }}
            disabled={createdList.length === 0}
            onClick={() => onConsult(createdList[0]?.orderCode)}
          >
            Consultar pedido
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
